import { Box, Typography } from '@mui/material';
import { Link } from 'react-router';

import { url } from '../../appRouter/url';
import { type StatusChangeResult, type VivaStatus, vivaStatus } from '../../dtos';
import { type SnackbarGenerator } from './snackbars.types';

export const generateVeranstaltungListingSuccessSnackbar: SnackbarGenerator<StatusChangeResult[]> = (
  veranstaltungen,
) => {
  const abgeschlosseneVeranstaltungen = veranstaltungen.filter((v) => v.status === vivaStatus.ABGESCHLOSSEN);
  const freigegebeneVeranstaltungen = veranstaltungen.filter((v) => v.status === vivaStatus.FREIGEGEBEN);
  const abgeschlossenString =
    abgeschlosseneVeranstaltungen.length > 0
      ? `${abgeschlosseneVeranstaltungen.length} Veranstaltung(en) wurde(n) erfolgreich auf den Status 'Planung abgeschlossen' geändert.`
      : '';
  const freigegebenString =
    freigegebeneVeranstaltungen.length > 0
      ? `${freigegebeneVeranstaltungen.length} Veranstaltung(en) wurde(n) erfolgreich auf den Status 'Planung freigegeben' geändert und nach SAP übermittelt.`
      : '';

  return {
    message: 'Status-Änderung erfolgreich',
    options: {
      variant: 'success',
      additional: `${abgeschlossenString} ${freigegebenString}`.trim(),
    },
  };
};

type GenerateVeranstaltungListingErrorSnackbarParams = {
  veranstaltungen: StatusChangeResult[];
  newStatus: VivaStatus;
  openLinksInNewTab: boolean;
};

export const generateVeranstaltungListingErrorSnackbar: SnackbarGenerator<
  GenerateVeranstaltungListingErrorSnackbarParams
> = ({ veranstaltungen, newStatus, openLinksInNewTab }: GenerateVeranstaltungListingErrorSnackbarParams) => {
  const freigegebenString = `${veranstaltungen.length} Veranstaltung(en) konnte(n) aufgrund von Fehlern nicht freigegeben und nach SAP übertragen werden. Der Status wurde zurückgesetzt. Bitte prüfe die Veranstaltung(en):`;
  const abgeschlossenString = `${veranstaltungen.length} Veranstaltung(en) konnte(n) aufgrund von Fehlern nicht auf den Status "Planung abgeschlossen" geändert werden. Der Status wurde zurückgesetzt. Bitte prüfe die Veranstaltung(en):`;

  return {
    message: 'Status-Änderung fehlgeschlagen',
    options: {
      variant: 'error',
      persist: true,
      additional: (
        <Box>
          <Typography>{newStatus === vivaStatus.ABGESCHLOSSEN ? abgeschlossenString : freigegebenString}</Typography>
          <ul>
            {veranstaltungen.map((veranstaltung) => (
              <li key={veranstaltung.akaVeranstaltungId}>
                <Link
                  to={url.veranstaltung.details(veranstaltung.akaVeranstaltungId)}
                  target={openLinksInNewTab ? '_blank' : '_self'}
                  rel="noopener"
                >
                  {veranstaltung.akaVeranstaltungId}
                </Link>
              </li>
            ))}
          </ul>
        </Box>
      ),
    },
  };
};

export const generateVeranstaltungListingDeleteSuccessSnackbar: SnackbarGenerator<{ successfulDeletions: string[] }> = (
  props,
) => ({
  message: 'Veranstaltung(en) erfolgreich gelöscht und andere Systeme über Löschung informiert',
  options: {
    variant: 'success',
    additional: (
      <Box>
        <Typography>Die folgenden Veranstaltung(en) wurden erfolgreich gelöscht:</Typography>
        <ul>
          {props.successfulDeletions.map((id) => (
            <li key={id}>{id}</li>
          ))}
        </ul>
      </Box>
    ),
  },
});

export const generateVeranstaltungListingDeleteErrorSnackbar: SnackbarGenerator<{ failedDeletions: string[] }> = (
  props,
) => ({
  message: 'Fehler beim Löschen der Veranstaltung(en)',
  options: {
    variant: 'error',
    persist: true,
    additional: (
      <Box>
        <Typography>Die folgenden Veranstaltung(en) konnten nicht gelöscht werden:</Typography>
        <ul>
          {props.failedDeletions.map((id) => (
            <li key={id}>
              <Link to={url.veranstaltung.details(id)} target="_blank" rel="noopener">
                {id}
              </Link>
            </li>
          ))}
        </ul>
      </Box>
    ),
  },
});

export { statusChangeErrorMessage } from './updateStatusOfVeranstaltungSnackbars';
